import { useEffect, useState, useRef, useCallback } from "react";
import { ethers } from "ethers";
import { createTheme, ThemeProvider } from "@material-ui/core";
import { SnackbarProvider } from "notistack";
import {
  getAccount,
  getFactory,
  getRouter,
  getNetwork,
  getWeth,
} from "./ethereumFunctions";
import COINS from "./constants/coins";
import * as chains from "./constants/chains";

const theme = createTheme({
  palette: {
    primary: {
      main: "#008938",
      contrastText: "#000",
    },
    secondary: {
      main: "#9e9e9e",
      contrastText: "#000",
    },
  },
});

const Web3Provider = (props) => {
  const [isConnected, setConnected] = useState(false);
  const network = useRef({
    provider: null,
    signer: null,
    account: null,
    coins: [],
    chainID: null,
    router: null,
    factory: null,
    weth: null,
  });

  // Function to load coins regardless of wallet connection
  const loadCoins = useCallback(() => {
    const chainId = network.current.chainID;
    if (chainId) {
      const coins = COINS.get(chainId) || []; // Load coins for the current chain
      network.current.coins = coins; // Set coins in the network ref
    }
  }, []);

  // Function to set up the connection to the Ethereum network
  const setupConnection = useCallback(async () => {
    if (!window.ethereum) {
      console.log("No wallet provider found.");
      return;
    }

    try {
      const provider = new ethers.providers.Web3Provider(window.ethereum);
      const signer = provider.getSigner();

      const account = await getAccount();
      const chainId = await getNetwork(provider);

      // Update network object
      network.current.provider = provider;
      network.current.signer = signer;
      network.current.account = account;
      network.current.chainID = chainId;

      if (chains.networks.includes(chainId)) {
        // Load coins immediately
        loadCoins(); // Load coins even if wallet is not connected

        // Set router, WETH, and factory addresses based on chainId
        const router = getRouter(chains.routerAddress.get(chainId), signer);
        const wplsAddress = await router.WPLS();
        const weth = getWeth(wplsAddress, signer);
        const factoryAddress = await router.factory();
        const factory = getFactory(factoryAddress, signer);

        // Update network object with retrieved values
        network.current.router = router;
        network.current.coins[0].address = wplsAddress;
        network.current.weth = weth;
        network.current.factory = factory;

        // Save connection state to localStorage
        localStorage.setItem('account', account);
        localStorage.setItem('chainId', chainId);

        setConnected(true);
      } else {
        console.log("Connected to the wrong network.");
        setConnected(false);
      }
    } catch (error) {
      console.error("Error setting up connection:", error);
      setConnected(false);
    }
  }, [loadCoins]);

  // Effect to load coins on mount
  useEffect(() => {
    loadCoins();
  }, [loadCoins]);

  // Effect to check for existing connection state on mount
  useEffect(() => {
    const storedAccount = localStorage.getItem('account');
    const storedChainId = localStorage.getItem('chainId');

    if (storedAccount && storedChainId) {
      // Set the account and chain ID back to the network ref
      network.current.account = storedAccount;
      network.current.chainID = parseInt(storedChainId, 10);
      setupConnection(); // Try to set up the connection again
    }
  }, [setupConnection]);

  // Effect to listen for wallet connection changes
  useEffect(() => {
    const handleAccountsChanged = (accounts) => {
      if (accounts.length === 0) {
        console.log("Please connect to MetaMask.");
        setConnected(false);
        localStorage.removeItem('account');
        localStorage.removeItem('chainId');
      } else {
        setupConnection(); // Trigger connection if accounts change
      }
    };

    const handleChainChanged = () => {
      setupConnection(); // Trigger connection if chain changes
    };

    if (window.ethereum) {
      window.ethereum.on("accountsChanged", handleAccountsChanged);
      window.ethereum.on("chainChanged", handleChainChanged);
    }

    // Cleanup event listeners on unmount
    return () => {
      if (window.ethereum) {
        window.ethereum.removeListener("accountsChanged", handleAccountsChanged);
        window.ethereum.removeListener("chainChanged", handleChainChanged);
      }
    };
  }, [setupConnection]);

  return (
    <ThemeProvider theme={theme}>
      <SnackbarProvider maxSnack={3}>
        <div>
          {props.render(network.current)}
          {!isConnected && (
            <span
              onClick={setupConnection}
              style={{ cursor: 'pointer', color: '#008938', textDecoration: 'underline' }}
            >
            </span>
          )}
        </div>
      </SnackbarProvider>
    </ThemeProvider>
  );
};

export default Web3Provider;
